import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text, useTheme } from '@Sage/hoi-poi-ui';
import { alphaColor, getVar } from '@Sage/ssm-utils';
import 'chart.js/auto';
import { Doughnut } from 'react-chartjs-2';

import './DoughnutChart.scss';

const propTypes = {
    arcBorderRadius: PropTypes.number,
    arcSpacing: PropTypes.number,
    labels: PropTypes.array,
    data: PropTypes.array,
    colors: PropTypes.array,
    centerLabel: PropTypes.string,
    centerValueColor: PropTypes.string,
    centerValue: PropTypes.string,
    options: PropTypes.object,
};

const DoughnutChart = memo(function DoughnutChart({
    labels,
    data,
    colors,
    centerLabel,
    centerValue,
    centerValueColor,
    options = {},
    arcBorderRadius = 4,
    arcSpacing = 1,
}) {
    const theme = useTheme();

    const chartData = useMemo(
        () => ({
            labels,
            datasets: [
                {
                    data,
                    backgroundColor: colors,
                    hoverBackgroundColor: colors,
                    borderWidth: 0,
                    borderRadius: arcBorderRadius,
                    spacing: arcSpacing,
                    selfJoin: false,
                },
            ],
        }),
        [arcBorderRadius, arcSpacing, colors, data, labels],
    );

    const defaultOptions = useMemo(() => {
        const tooltipText = getVar(theme.colors.primary.white);
        return {
            cutout: '75%',
            layout: {
                padding: 0,
            },
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: true,
                    backgroundColor: getVar(theme.colors.actionMinor[550]),
                    titleColor: tooltipText,
                    bodyColor: tooltipText,
                    footerColor: tooltipText,
                    borderColor: alphaColor(theme.colors.primary.white, 0.18),
                    borderWidth: 1,
                    padding: 12,
                    displayColors: true,
                    usePointStyle: true,
                    multiKeyBackground: alphaColor(theme.colors.primary.white, 0.2),
                    callbacks: {
                        labelTextColor: () => tooltipText,
                    },
                },
            },
        };
    }, [theme]);

    const mergedOptions = useMemo(
        () => ({
            ...defaultOptions,
            ...options,
        }),
        [defaultOptions, options],
    );

    return (
        <div className="fm-doughnut-chart">
            <div className="fm-doughnut-chart__slot">
                <Doughnut data={chartData} options={mergedOptions} />
                {centerValue ? (
                    <div className="fm-doughnut-chart__overlay">
                        <div className="fm-doughnut-chart__center">
                            <Text
                                as="div"
                                bold
                                className="fm-doughnut-chart__value"
                                color={centerValueColor}
                                type="h3"
                            >
                                {centerValue}
                            </Text>
                            {centerLabel ? (
                                <Text as="div" className="fm-doughnut-chart__label" type="caption">
                                    {centerLabel}
                                </Text>
                            ) : null}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
});

DoughnutChart.displayName = 'DoughnutChart';
DoughnutChart.propTypes = propTypes;

export default DoughnutChart;
